import React from 'react';
import { absoluteMediaUrl } from '../media';

/**
 * UserAvatar — renders an uploaded avatar image when the user has one,
 * otherwise falls back to initials. Single source of truth for any
 * letter-avatar render site in the app.
 *
 * Two usage modes:
 *
 * 1. Default DAS styling (no className passed):
 *      <UserAvatar user={u} size="sm" isYou={true} />
 *    Renders with .dt-avatar (+ .dt-avatar-sm + .dt-av-you).
 *
 * 2. Custom className (standard project + per-component avatars):
 *      <UserAvatar user={u} className="avatar avatar-xs" style={{...}} />
 *    Renders with whatever class the caller passes. The 'isYou' prop
 *    still adds .dt-av-you on top if set.
 *
 * In both modes, the image-mode helper class .uavatar-clip is added.
 * That class only contributes overflow:hidden + img positioning rules,
 * so the caller's visual styling (size, bg, border) is preserved.
 *
 * ── WR-023: former members ───────────────────────────────────────────
 *
 * A person removed from the organization keeps their name on the work they
 * did — comments, authored tasks, archived assignments — and that
 * attribution must keep rendering. But it helps to be able to tell at a
 * glance that the name belongs to somebody who is no longer around, so an
 * avatar for a deactivated user gets `.uavatar-former` and a tooltip that
 * says so.
 *
 * Done here because this component is the single render site for every
 * avatar in the app, so one change covers comment authors, task assignee
 * chips, avatar stacks, subtask rows and the DAS views at once. It relies
 * on `active` being present in the API's projection: the routes that serve
 * historical attribution select it, and where it is absent the value is
 * undefined and nothing changes — so this can never mislabel a live user.
 */

function deriveInitials(name, email) {
  if (name && name.trim()) {
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  if (email) return email.slice(0, 2).toUpperCase();
  return '?';
}

function avatarUrl(user) {
  if (!user || !user.avatar) return null;
  // Avatar paths are stored relative (/uploads/...) — prefix with origin and
  // sign with the media token, since uploads are no longer public (WR-014).
  // Fully-qualified URLs (S3 etc.) are passed through unchanged.
  if (/^https?:\/\//i.test(user.avatar)) return user.avatar;
  return absoluteMediaUrl(user.avatar);
}

function UserAvatar({
  user,
  className,
  size = 'md',
  isYou = false,
  title,
  style,
}) {
  // Build the class list. If caller passed no className, use the DAS
  // dt-avatar default. Otherwise honour the caller's class entirely.
  let classes;
  if (className) {
    classes = isYou ? `${className} dt-av-you` : className;
  } else {
    const sizeClass = size === 'sm' ? 'dt-avatar-sm' : '';
    const youClass  = isYou ? 'dt-av-you' : '';
    classes = ['dt-avatar', sizeClass, youClass].filter(Boolean).join(' ');
  }
  classes += ' uavatar-clip';

  // Strictly `=== false`: a projection without `active` yields undefined,
  // which must read as "live", never as "former".
  const isFormer = user?.active === false;
  if (isFormer) classes += ' uavatar-former';

  const url = avatarUrl(user);

  const baseTooltip = title || user?.name || user?.email || '';
  const tooltip = isFormer && baseTooltip
    ? `${baseTooltip} — former member`
    : baseTooltip;

  if (url) {
    return (
      <span
        className={classes}
        title={tooltip}
        aria-label={tooltip}
        style={style}
      >
        <img
          src={url}
          alt=""
          className="uavatar-img"
          onError={(e) => {
            // If the image URL fails, fall back to initials inline so we
            // never show a broken-image icon on a user row.
            const span = e.target.parentNode;
            if (span) {
              e.target.remove();
              span.textContent = deriveInitials(user?.name, user?.email);
            }
          }}
        />
      </span>
    );
  }

  return (
    <span className={classes} title={tooltip} aria-label={tooltip} style={style}>
      {deriveInitials(user?.name, user?.email)}
    </span>
  );
}

export default UserAvatar;
export { deriveInitials, avatarUrl };
